import { createDomElementHandler, createSvg } from "@/scripts/utils/dom.utils";
import { PurchaseCtaModel } from "./purchaseCta.model";
import "./purchaseCta.style.scss";

interface PurchaseCtaViewOptions {
  /** Preserve the card's contextual selectors without coupling other consumers to them. */
  cardClasses?: boolean;
  unavailableLabel?: string;
  triggerClasses?: string[];
  menuPlacement?: "anchored" | "viewport";
  onOpenChange?: (open: boolean) => void;
}

/** DOM view owned by its consumer, which connects/disconnects it with its own lifecycle. */
export class PurchaseCtaView {
  readonly element: HTMLElement;
  private readonly trigger: HTMLElement;
  private readonly menu?: HTMLElement;
  private connected = false;

  constructor(model: PurchaseCtaModel, private readonly options: PurchaseCtaViewOptions = {}) {
    const classes = ["ab-purchase-cta__trigger", "ab-btn", "ab-btn--has-icon", "ab-btn--amz-boost",
      ...(options.triggerClasses ?? [])];
    if (options.cardClasses) classes.push("ab-pc-1__cta", "ab-pc-1__cta--aff");
    this.trigger = createDomElementHandler(model.type === "selector" ? "button" : "a", classes);
    this.trigger.appendChild(createDomElementHandler("span", "ab-btn__icon"));
    const text = createDomElementHandler("span", "ab-btn__text");
    text.textContent = model.type === "unavailable" && options.unavailableLabel ? options.unavailableLabel : "Vedi su Amazon";
    this.trigger.appendChild(text);
    this.element = createDomElementHandler("div", "ab-purchase-cta");
    this.element.appendChild(this.trigger);

    if (model.type === "direct") {
      this.trigger.setAttribute("href", model.url);
      this.trigger.setAttribute("target", "_blank");
      this.trigger.setAttribute("rel", "noopener noreferrer");
    } else if (model.type === "unavailable") {
      this.trigger.setAttribute("disabled", "true");
      this.trigger.setAttribute("aria-disabled", "true");
    } else {
      this.element.classList.add("ab-purchase-cta--selector");
      this.trigger.setAttribute("type", "button");
      this.trigger.setAttribute("aria-expanded", "false");
      this.trigger.setAttribute("aria-label", "Scegli attacco su Amazon");
      const chevron = createSvg("icon-chevron-down");
      chevron.classList.add("ab-purchase-cta__chevron");
      this.trigger.appendChild(chevron);
      this.menu = createDomElementHandler("div", "ab-purchase-cta__menu");
      this.menu.hidden = true;
      if (options.menuPlacement === "viewport") this.menu.classList.add("ab-purchase-cta__menu--viewport");
      model.options.forEach(({ mount, url }) => {
        const option = createDomElementHandler(url?.length ? "a" : "span", "ab-purchase-cta__option");
        if (url?.length) {
          option.setAttribute("href", url);
          option.setAttribute("target", "_blank");
          option.setAttribute("rel", "noopener noreferrer");
        } else {
          option.classList.add("ab-purchase-cta__option--disabled");
          option.setAttribute("aria-disabled", "true");
        }
        const header = createDomElementHandler("span", "ab-purchase-cta__mount-header");
        const name = createDomElementHandler("strong", "ab-purchase-cta__mount-name");
        name.textContent = mount.name;
        header.appendChild(name);
        if (!url?.length) {
          const unavailable = createDomElementHandler("span", "ab-purchase-cta__unavailable");
          unavailable.textContent = "Non disponibile";
          header.appendChild(unavailable);
        }
        const description = createDomElementHandler("span", "ab-purchase-cta__description");
        description.textContent = mount.description;
        option.append(header, description);
        this.menu.appendChild(option);
      });
      this.element.appendChild(this.menu);
      if (options.cardClasses) {
        this.element.classList.add("ab-pc-1__affiliate-selector");
        this.menu.classList.add("ab-pc-1__affiliate-menu");
        chevron.classList.add("ab-pc-1__affiliate-menu-chevron");
        this.menu.querySelectorAll('[class*="ab-purchase-cta__"]').forEach((node) => {
          Array.from(node.classList).filter((name) => name.startsWith("ab-purchase-cta__"))
            .forEach((name) => node.classList.add(name.replace("ab-purchase-cta__", "ab-pc-1__affiliate-menu-")));
        });
      }
    }
  }

  connect() {
    if (this.connected || !this.menu) return;
    this.connected = true;
    this.trigger.addEventListener("click", this.onTriggerClick);
    this.menu.addEventListener("click", this.onMenuClick);
    document.addEventListener("click", this.onDocumentClick, true);
    // Capture runs before the modal's Escape handler, regardless of registration order.
    document.addEventListener("keydown", this.onKeydown, true);
  }

  disconnect() {
    this.close();
    this.connected = false;
    this.trigger.removeEventListener("click", this.onTriggerClick);
    this.menu?.removeEventListener("click", this.onMenuClick);
    document.removeEventListener("click", this.onDocumentClick, true);
    document.removeEventListener("keydown", this.onKeydown, true);
  }

  close = () => {
    if (!this.menu || this.menu.hidden) return;
    this.menu.hidden = true;
    this.trigger.setAttribute("aria-expanded", "false");
    this.element.classList.remove("ab-purchase-cta--open");
    window.removeEventListener("resize", this.positionMenu);
    document.removeEventListener("scroll", this.positionMenu, true);
    this.options.onOpenChange?.(false);
  };

  private onTriggerClick = () => {
    if (!this.menu.hidden) return this.close();
    this.menu.hidden = false;
    this.trigger.setAttribute("aria-expanded", "true");
    this.element.classList.add("ab-purchase-cta--open");
    if (this.options.menuPlacement === "viewport") {
      this.positionMenu();
      window.addEventListener("resize", this.positionMenu);
      document.addEventListener("scroll", this.positionMenu, true);
    }
    this.options.onOpenChange?.(true);
  };

  private onDocumentClick = (event: MouseEvent) => {
    if (!this.element.contains(event.target as Node)) this.close();
  };

  private onMenuClick = (event: MouseEvent) => {
    if ((event.target as HTMLElement).closest("a")) this.close();
  };

  private onKeydown = (event: KeyboardEvent) => {
    if (event.key !== "Escape" || this.menu.hidden) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.close();
    this.trigger.focus();
  };

  private positionMenu = () => {
    if (!this.menu || this.menu.hidden) return;
    const trigger = this.trigger.getBoundingClientRect();
    let left = 8;
    let right = window.innerWidth - 8;
    let top = 8;
    // Fixed placement escapes overflow clipping; intersect scrollports to keep
    // the popup within the visible sheet, including its modal body.
    for (let parent = this.element.parentElement; parent; parent = parent.parentElement) {
      const style = getComputedStyle(parent);
      const rect = parent.getBoundingClientRect();
      if (style.overflowX !== "visible") {
        left = Math.max(left, rect.left + 4);
        right = Math.min(right, rect.right - 4);
      }
      if (style.overflowY !== "visible") top = Math.max(top, rect.top + 4);
    }
    const width = Math.max(0, Math.min(window.innerWidth <= 575 ? trigger.width : 416, right - left));
    this.menu.style.width = `${width}px`;
    this.menu.style.left = `${Math.max(left, Math.min(trigger.right - width, right - width))}px`;
    this.menu.style.bottom = `${window.innerHeight - trigger.top + 8}px`;
    this.menu.style.maxHeight = `${Math.max(0, Math.min(320, trigger.top - top - 8))}px`;
  };
}
